import React, { useContext, useState } from "react";
import { AppContext } from "../AppContext";
import { useGetMPInfo } from "../hooks/useGetMPInfo";

import generateMessageBody from "../utils/generateMessageBody";
import { profanityList } from "../utils/profanityList";
import ReCaptcha from "./ReCaptcha";
import EmailSentConfirmation from "./EmailSentConfirmation";

interface EmailFormProps {
  setFormStep: React.Dispatch<React.SetStateAction<number>>;
  onEmailSent?: () => void;
}

interface EmailToken {
  token: string;
  expires: number;
}

// Update the profanity filter function
export const profanityFilter = (text: string) => {
  const regex = new RegExp(profanityList.join("|"), "gi");
  return text.replace(regex, "****");
};

// Declare grecaptcha on window for TypeScript
declare global {
  interface Window {
    grecaptcha: any;
  }
}

const EmailForm: React.FunctionComponent<EmailFormProps> = ({
  setFormStep,
  onEmailSent,
}) => {
  const {
    dispatch,
    loading,
    postcode,
    mp,
    formValues,
    handleUpdateFormValues,
    sendingEmail,
    handleSetSendingEmail,
  } = useContext(AppContext);

  const [emailToken, setEmailToken] = useState<EmailToken | null>(null);
  const [emailError, setEmailError] = useState("");
  const [rewrittenMessage, setRewrittenMessage] = useState("");
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [profanityError, setProfanityError] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [emailSentSuccessfully, setEmailSentSuccessfully] = useState(false);

  useGetMPInfo({ dispatch: dispatch!, postcode: postcode ?? "" });

  const getEmailToken = async () => {
    try {
const response = await fetch("/emailyourmp/send_email.php/get-token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userEmail: formValues?.email,
          mpEmail: mp?.email,
        }),
      });
      const responseBodyText = await response.text(); // Read body as text ONCE

      if (!response.ok) {
        console.error(
          `Send email HTTP error! Status: ${response.status}, Body: ${responseBodyText}`
        );
        // You might want to try parsing responseBodyText as JSON here if errors are JSON formatted
        try {
          const errorData = JSON.parse(responseBodyText);
          throw new Error(
            errorData.error || `Server error: ${response.status}`
          );
        } catch (e) {
          throw new Error(
            `Server error: ${response.status} - ${responseBodyText}`
          );
        }
      }

      const data = JSON.parse(responseBodyText); // Parse the text
      if (data.success) {
        setEmailToken(data.data);
        return data.data.token;
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error("Error getting email token:", error);
      throw error;
    }
  };


  const handleGenerateEmail = async () => {
    if (!recaptchaToken) {
      alert("Please complete the reCAPTCHA verification.");
      if (window.grecaptcha) { window.grecaptcha.reset(); }
      return;
    }
    handleSetSendingEmail?.(true);

    const {
      greeting,
      emailBodyForRewriting,
      formattedConcernBody,
    } = generateMessageBody({
      firstName: formValues?.firstName,
      surname: formValues?.surname,
      postcode: postcode,
      email: formValues?.email,
      mpName: mp?.name,
      drivesEV: formValues?.drivesEV,
      concerns: formValues?.concerns,
    });

    try {
      let fullMessage = `${greeting}\n\n${emailBodyForRewriting}${formattedConcernBody}`;

      // Replace subsequent "I am concerned" with "I am also concerned"
      let firstFound = false;
      fullMessage = fullMessage.replace(/I am concerned/g, (match) => {
        if (!firstFound) { firstFound = true; return match; }
        return "I am also concerned";
      });

      setRewrittenMessage(fullMessage);
      handleUpdateFormValues?.({
        subject: "Supporting EV drivers in your constituency",
        message: fullMessage,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      alert(`Error generating email: ${errorMessage}`);
    } finally {
      handleSetSendingEmail?.(false);
      setRecaptchaToken(null);
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleRecaptchaVerify = (token: string | null) => {
    setRecaptchaToken(token);
  };

  if (loading) return <p>Loading...</p>;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();


    if (profanityError) {
      alert("Please remove any inappropriate language before sending.");
      return;
    }

    if (!mp?.email) {
      alert("We were unable to find your MP's email address. Please try searching your postcode again, or contact us at info@evaengland.org.uk for help.");
      return;
    }

    try {
      handleSetSendingEmail?.(true);


      // Get a fresh token or use existing one if still valid
      const token =
        emailToken && emailToken.expires > Date.now() / 1000
          ? emailToken.token
          : await getEmailToken();

      const emailData = {
        to_name: mp?.name,
        to_email: mp?.email,
        from_name: `${formValues?.firstName} ${formValues?.surname}`,

        from_email: formValues?.email,
        reply_to: formValues?.email,
        subject:
          formValues?.subject ||
          "Supporting EV drivers in your constituency",
        body: rewrittenMessage,
      };

      // Send email
      const emailResponse = await fetch(
        "/emailyourmp/send_email.php/send-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token,
            emailData,
          }),
        }
      );

      const emailResult = await emailResponse.json();
      if (!emailResult.success) {
        throw new Error(emailResult.error);
      }

      // If email sent successfully, submit user data to Mnovate
      const mnovateResponse = await fetch("/emailyourmp/submit_user_data.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: formValues?.firstName,
          surname: formValues?.surname,
          email: formValues?.email,
          postcode: postcode,
          drivesEV: formValues?.drivesEV,
          concerns: formValues?.concerns || [],
          optIn: formValues?.optIn,
        }),
      });

      const mnovateResult = await mnovateResponse.json();
      if (!mnovateResult.success) {
        throw new Error(mnovateResult.error);
      }


setEmailSentSuccessfully(true);
      onEmailSent?.();
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      alert(`An error occurred: ${errorMessage}`);
    } finally {
      handleSetSendingEmail?.(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    handleUpdateFormValues?.({ [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    handleUpdateFormValues?.({
      concerns: checked
        ? [...(formValues?.concerns || []), name]
        : (formValues?.concerns || []).filter((concern) => concern !== name),
    });
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    // Find the first bad word that appears as a whole word
    const foundBadWord = profanityList.find((word) => {
      const wordRegex = new RegExp(`\\b${word}\\b`, "i");
      return wordRegex.test(value);
    });
    const containsProfanity = !!foundBadWord;

    if (containsProfanity) {
    }

    setProfanityError(containsProfanity);
    setRewrittenMessage(value);
    handleUpdateFormValues?.({ message: value });
  };

  const disabled =
    loading ||
    !mp ||
    !postcode ||
    !formValues?.firstName ||
    !formValues?.surname ||
    !formValues?.email ||

    !formValues?.drivesEV ||

!formValues?.concerns?.length ||
    profanityError;

  if (emailSentSuccessfully) {
    return (
      <EmailSentConfirmation
        mpName={mp?.name || ""}
        userEmail={formValues?.email || ""}
      />
    );
  }

  return (
    <div className="font-light px-4 pb-4">
      <div className="mt-4 mb-4 flex items-start">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-3">
            <span className="font-semibold text-sm sm:text-base">Your MP :</span>
            <span className="text-sm sm:text-base">{mp?.name}</span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className="font-semibold text-sm sm:text-base">Your Constituency :</span>
            <span className="text-sm sm:text-base">{mp?.constituency}</span>
          </div>
        </div>
      </div>
      <form className="mt-4" onSubmit={handleSubmit}>
        <div className="flex flex-col md:flex-row md:space-x-20 mt-4">
          <div className="flex flex-col w-full mb-4 md:mb-0">
            <label htmlFor="firstName" className="font-semibold">Your First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              placeholder="Enter your first name"
              className="border-2 rounded-md p-2 mt-1 w-full md:w-auto"
              onChange={handleInputChange}
              value={formValues?.firstName || ""}
              required
            />
          </div>
          <div className="flex flex-col w-full">
            <label htmlFor="surname" className="font-semibold">Your Surname</label>
            <input
              type="text"
              id="surname"
              name="surname"
              placeholder="Enter your surname"
              className="border-2 rounded-md p-2 mt-1 w-full md:w-auto"
              onChange={handleInputChange}
              value={formValues?.surname || ""}
              required
            />
          </div>
        </div>
        <div className="mt-4">
          <div className="flex flex-col mb-4">
            <label htmlFor="email" className="font-semibold">Your Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter your email"
              className="border-2 rounded-md p-2 mt-1"
              onChange={handleInputChange}
              onFocus={() => setEmailError("")}
              onBlur={(e) => {
                if (e.target.value === "") return;
                if (!validateEmail(e.target.value)) {
                  setEmailError("Please enter a valid email address.");
                } else {
                  setEmailError("");
                }
              }}
              value={formValues?.email || ""}
              required
            />
            {emailError && <p className="text-red-500">{emailError}</p>}
          </div>

        </div>
        <div className="mt-4">
          <label className="font-semibold">Do you drive an EV?</label>
          <div className="flex mt-2">
            <input
              type="radio"
              id="drivesEV-yes"
              name="drivesEV"
              value="yes"
              className="me-2"
              onChange={handleInputChange}
              required
            />
            <label htmlFor="drivesEV-yes">Yes</label>
          </div>
          <div>
            <input
              type="radio"
              id="drivesEV-no"
              name="drivesEV"
              value="no"
              className="me-2"
              onChange={handleInputChange}
              required
            />
            <label htmlFor="drivesEV-no">No</label>
          </div>
        </div>



        <div className="mt-4">
          <label className="font-semibold">What issues are you most concerned about? (Select up to 3 max)</label>
          <div className="mt-2">
            <div>
              <input
                type="checkbox"
                id="concern-zev-mandate"
                name="Changes to the ZEV Mandate"
                onChange={handleCheckboxChange}
                checked={formValues?.concerns?.includes("Changes to the ZEV Mandate")}
                disabled={(formValues?.concerns?.length ?? 0) >= 3 && !formValues?.concerns?.includes("Changes to the ZEV Mandate")}
              />
              <label htmlFor="concern-zev-mandate" className="ml-2">
                Changes to the ZEV Mandate
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                id="concern-running-costs"
                name="Running costs, including potential pay per mile tax"
                onChange={handleCheckboxChange}
                checked={formValues?.concerns?.includes("Running costs, including potential pay per mile tax")}
                disabled={(formValues?.concerns?.length ?? 0) >= 3 && !formValues?.concerns?.includes("Running costs, including potential pay per mile tax")}
              />
              <label htmlFor="concern-running-costs" className="ml-2">
                Running costs, including potential pay per mile tax
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                id="concern-upfront-cost"
                name="Upfront cost of vehicles"
                onChange={handleCheckboxChange}
                checked={formValues?.concerns?.includes("Upfront cost of vehicles")}
                disabled={(formValues?.concerns?.length ?? 0) >= 3 && !formValues?.concerns?.includes("Upfront cost of vehicles")}
              />
              <label htmlFor="concern-upfront-cost" className="ml-2">
                Upfront cost of vehicles
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                id="concern-battery-health"
                name="Battery health"
                onChange={handleCheckboxChange}
                checked={formValues?.concerns?.includes("Battery health")}
                disabled={(formValues?.concerns?.length ?? 0) >= 3 && !formValues?.concerns?.includes("Battery health")}
              />
              <label htmlFor="concern-battery-health" className="ml-2">
                Battery health
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                id="concern-cost-of-charging"
                name="Cost of charging"
                onChange={handleCheckboxChange}
                checked={formValues?.concerns?.includes("Cost of charging")}
                disabled={(formValues?.concerns?.length ?? 0) >= 3 && !formValues?.concerns?.includes("Cost of charging")}
              />
              <label htmlFor="concern-cost-of-charging" className="ml-2">
                Cost of charging
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                id="concern-reliability-accessibility"
                name="Reliability and accessibility of charge points"
                onChange={handleCheckboxChange}
                checked={formValues?.concerns?.includes("Reliability and accessibility of charge points")}
                disabled={(formValues?.concerns?.length ?? 0) >= 3 && !formValues?.concerns?.includes("Reliability and accessibility of charge points")}
              />
              <label htmlFor="concern-reliability-accessibility" className="ml-2">
                Reliability and accessibility of charge points
              </label>
            </div>
          </div>
        </div>
        <ReCaptcha onVerify={handleRecaptchaVerify} />
        <button
          type="button"
          className={`mt-4 font-bold py-2 px-4 rounded uppercase ${
            disabled || sendingEmail
              ? "bg-slate-400 text-white cursor-not-allowed"
              : "bg-[#00b3bd] text-white hover:shadow-xl"
          }`}
          onClick={handleGenerateEmail}
          disabled={disabled || sendingEmail}
        >
          {sendingEmail ? "GENERATING..." : "GENERATE EMAIL"}
        </button>

        {rewrittenMessage && (
          <>
            {/* Email Preview */}
            <div className="flex flex-col mb-4 mt-4">
              <label htmlFor="subject" className="font-semibold">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                placeholder="Edit subject line"
                className="border-2 rounded-md p-2 mt-1"
                onChange={handleInputChange}
                value={
                  formValues?.subject ||
                  "Support electric vehicle drivers in your constituency | EVA England"
                }
              />
            </div>
            <div className="flex flex-col mb-4 mt-4">
              <label htmlFor="message" className="font-semibold">Message</label>
              <textarea
                id="message"
                name="message"
                placeholder="Enter your message"
                className="border-2 rounded-md p-2 mt-1 whitespace-pre-line"
                style={{ minHeight: "16rem" }}
                value={rewrittenMessage}
                onChange={handleMessageChange}
              />
              {profanityError && (
                <p className="text-red-500">
                  Please remove any inappropriate language before sending.
                </p>
              )}
            </div>
            <div>
              <label>
                Please check the email preview above before sending. You can
                edit the email as you see fit.
              </label>
            </div>
            <div className="flex flex-col mt-4">
              <label htmlFor="opt-in" className="font-semibold">Become a supporter and follow this campaign's progress?</label>
              <div className="mt-2">
                <input
                  type="radio"
                  id="optIn-yes"
                  name="optIn"
                  value="yes"
                  className="me-2"
                  onChange={handleInputChange}
                  checked={formValues?.optIn === "yes"}
                  required
                />
                <label htmlFor="optIn-yes">Yes, sign me up</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="optIn-no"
                  name="optIn"
                  value="no"
                  className="me-2"
                  onChange={handleInputChange}
                  checked={formValues?.optIn === "no"}
                />
                <label htmlFor="optIn-no">No thanks</label>
              </div>
            </div>

            <div className="my-4">
              <input
                type="checkbox"
                id="privacy-policy"
                checked={privacyPolicyAccepted}
                onChange={(e) => setPrivacyPolicyAccepted(e.target.checked)}
                required
              />
              <label htmlFor="privacy-policy" className="ml-2">
                By sending this message, you agree to our{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  className="text-[#00b3bd] underline"
                >
                  Privacy Policy
                </a>
                .
              </label>
            </div>
            <div>
              <button
                type="submit"
                className={`mt-4 font-bold py-2 px-4 rounded uppercase ${
                  disabled ||
                  !formValues?.optIn ||
                  sendingEmail ||
                  !privacyPolicyAccepted
                    ? "bg-slate-400 text-white cursor-not-allowed"
                    : "bg-[#00b3bd] text-white hover:shadow-xl"
                }`}
                disabled={
                  disabled ||
                  !formValues?.optIn ||
                  sendingEmail ||
                  !rewrittenMessage ||
                  !privacyPolicyAccepted
                }
              >
                {sendingEmail ? "SENDING..." : "SEND"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default EmailForm;
